import { applySnapshot, Instance, types as t } from 'mobx-state-tree';
import { Element, Text } from 'slate';

import { ELEMENT_H2 } from '../../slatePlugins';
import { ELEMENT_H3, ELEMENT_H4 } from '../../slatePlugins/elements/heading/defaults';
import { ELEMENT_PARAGRAPH } from '../../slatePlugins/elements/paragraph/defaults';
import { MARK_BOLD } from '../../slatePlugins/marks/bold/defaults';
import { MARK_ITALIC } from '../../slatePlugins/marks/italic/defaults';
import { MARK_STRIKETHROUGH } from '../../slatePlugins/marks/strikethrough/defaults';
import { ELEMENT_BORDER_SHADING } from '../DolEditor/elements/border-shading/defaults';
import { ELEMENT_HIGHLIGHT } from '../DolEditor/elements/highlight/defaults';
import { ELEMENT_HOVER_CARD } from '../DolEditor/elements/hover-card/defaults';
import { ELEMENT_HYPER_LINK } from '../DolEditor/elements/hyper-link/defaults';
import { ELEMENT_ROUNDED } from '../DolEditor/elements/rounded/defaults';
import { TableElement } from '../DolEditor/elements/table/types';
import { MARK_TEXT_COLOR } from '../DolEditor/marks/text-color/defaults';
import { MARK_UNDERLINE_COLOR } from '../DolEditor/marks/underline-color/defaults';
import { BLANK_V2 } from './plugins-common/BlankV2/types';
import { PLUGIN_EXPLANATON_PRESENT_TYPES } from './plugins-common/Explanations/types';
import {
  ELEMENT_BULLET_POINT,
  ELEMENT_BULLET_POINT_CHECK,
  ELEMENT_BULLET_POINT_NUMBER,
  ELEMENT_BULLET_POINT_QUESTION,
  ELEMENT_BULLET_POINT_X,
} from './plugins/BulletPoint/BulletPointPlugin.types';
import {
  CARD_LOGIC_CELL_ELEMENT_TYPE,
  CARD_LOGIC_ELEMENT_TYPE,
  CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE,
  VOCAB_CARD,
} from './plugins/CardLogic/model/constants';
import {
  EXERCISE_MULTIPLE_CHOICE,
  MULTIPLE_CHOICE_PARAGRAPH_TOGGLE_TYPES,
} from './plugins/ExerciseMultipleChoice/types';
import { SHORT_ANSWER_PARAGRAPH_TOGGLE_TYPES } from './plugins/ExerciseShortAnswer/types';
import { EXERCISE_GAP_FILL, GAP_FILL_PARAGRAPH_TOGGLE_TYPES } from './plugins/GapFill/types';
import { ELEMENT_QUESTION_STYLE } from './plugins/QuestionStyle/QuestionStyle.types';
import { ELEMENT_QUOTE } from './plugins/Quote/QuotePlugin.types';
import { SAMPLE_SPECIAL_OBJECT_VOID } from './plugins/SpecialObject/IeltsSample/types';
import { ELEMENT_IMAGE_SLIDE_TEXT_INPUT } from './plugins/SpecialObject/ImageSlide/SlideText/ImageSlideTextPlugin.types';
import { SPECIAL_OBJECT_VOID } from './plugins/SpecialObject/types';
import {
  MARK_TEXT_TRANSFORM,
  TEXT_TRANSFORM_LOWER_CASE,
  TEXT_TRANSFORM_TITLE_CASE,
  TEXT_TRANSFORM_UPPER_CASE,
} from './plugins/TextTransform/types';
import { K12_ELEMENT_QUOTE } from './plugins/K12Quote/K12QuotePlugin.types';
import { MARK_CITE } from '../../slatePlugins/marks/cite/defaults';
import { MARK_ABBR } from '../../slatePlugins/marks/abbr/defaults';
import { MARK_Q } from '../../slatePlugins/marks/q/defaults';
import { MARK_UNDERLINE } from '../../slatePlugins/marks/underline/defaults';

export const initialBlogEditorSelectState = {
  isTextSelection: false,

  isInCardLogic: false,
  isDisableAll: false,
  isInCardLogicHeader: false,
  isInCardLogicCell: false,

  isInSpecialObject: false,
  isInSampleSpecialObject: false,
  isInSlideTextInput: false,

  isInExerciseMultipleChoice: false,
  isInExerciseGapFill: false,
  isInExerciseShortAnswer: false,
  isInBlankV2: false,
  isInPluginExplanation: false,

  isInTable: false,
  isInTableHeader: false,
  isInTableCell: false,

  // common element
  isParagraph: false,
  isHeading2: false,
  isHeading3: false,
  isHeading4: false,
  isRoundedText: false,
  isNumberingElement: false,
  isTitleCase: false,
  isUpperCase: false,
  isLowerCase: false,
  isHyperLink: false,
  isHoverCard: false,

  isInQuoteBlock: false,
  isInBulletPoint: false,
  isInBorderShading: false,

  // color
  currentTextColor: '',
  underlineColor: '',
  highlightElementColor: '',

  // marks
  isBold: false,
  isUnderline: false,
  isItalic: false,
  isStrikeThrough: false,
  isAbbr: false,
  isCite: false,
  isQ: false,

  // custom
  isExtraLearningPointEditor: false,
  isK12Quote: false,
};

export const BlogEditorSelectStateStore = t
  .model('BlogEditorSelectStateStore', initialBlogEditorSelectState)
  .actions((self) => ({
    reset() {
      applySnapshot(self, initialBlogEditorSelectState);
    },
    doneCheckSelectState() {},
    updateIsTextSelection(isTextSelection) {
      self.isTextSelection = isTextSelection;
    },
    setFieldValue(fieldKey, value) {
      self[fieldKey] = value;
    },

    markSelectionCheck({ node, path }) {
      // for element
      if (Element.isElement(node)) {
        switch (node?.type) {
          case ELEMENT_H2: {
            self.isHeading2 = true;
            break;
          }
          case ELEMENT_H3: {
            self.isHeading3 = true;
            break;
          }

          case ELEMENT_H4: {
            self.isHeading4 = true;
            break;
          }

          case ELEMENT_PARAGRAPH: {
            self.isParagraph = true;
            break;
          }

          case ELEMENT_ROUNDED: {
            self.isRoundedText = true;
            break;
          }

          case ELEMENT_QUESTION_STYLE: {
            self.isNumberingElement = true;
            break;
          }
          case ELEMENT_HIGHLIGHT: {
            self.highlightElementColor = node.highlightKey as string;
            break;
          }

          case ELEMENT_QUOTE: {
            self.isInQuoteBlock = true;
            break;
          }

          case K12_ELEMENT_QUOTE: {
            self.isK12Quote = true;
            break;
          }

          case ELEMENT_BULLET_POINT:
          case ELEMENT_BULLET_POINT_CHECK:
          case ELEMENT_BULLET_POINT_X:
          case ELEMENT_BULLET_POINT_QUESTION:
          case ELEMENT_BULLET_POINT_NUMBER: {
            self.isInBulletPoint = true;
            break;
          }

          case SPECIAL_OBJECT_VOID:
            self.isInSpecialObject = true;
            break;

          case EXERCISE_MULTIPLE_CHOICE:
            self.isInExerciseMultipleChoice = true;
            break;
          case 'paragraph-toggle': {
            if (Object.values(MULTIPLE_CHOICE_PARAGRAPH_TOGGLE_TYPES).includes(node.present)) {
              self.isInExerciseMultipleChoice = true;
            }

            if (Object.values(GAP_FILL_PARAGRAPH_TOGGLE_TYPES).includes(node.present)) {
              self.isInExerciseGapFill = true;
            }

            if (Object.values(PLUGIN_EXPLANATON_PRESENT_TYPES).includes(node.present)) {
              self.isInPluginExplanation = true;
            }

            if (Object.values(SHORT_ANSWER_PARAGRAPH_TOGGLE_TYPES).includes(node.present)) {
              self.isInExerciseShortAnswer = true;
            }
            break;
          }

          case EXERCISE_GAP_FILL:
            self.isInExerciseGapFill = true;
            break;

          case BLANK_V2:
            self.isInBlankV2 = true;
            break;

          case SAMPLE_SPECIAL_OBJECT_VOID:
            self.isInSampleSpecialObject = true;
            break;

          case ELEMENT_IMAGE_SLIDE_TEXT_INPUT:
            self.isInSlideTextInput = true;
            break;

          case ELEMENT_HYPER_LINK:
            self.isHyperLink = true;
            break;

          case ELEMENT_HOVER_CARD:
            self.isHoverCard = true;
            break;

          default:
        }

        // special case - h3 inside CardLogic
        if (node.h3Enabled) {
          self.isHeading3 = true;
        }
      }

      // for mark
      if (Text.isText(node)) {
        if (node?.[MARK_BOLD]) {
          self.isBold = true;
        }
        if (node?.[MARK_ITALIC]) {
          self.isItalic = true;
        }
        if (node?.[MARK_UNDERLINE]) {
          self.isUnderline = true;
        }
        if (node?.[MARK_UNDERLINE_COLOR]) {
          self.underlineColor = (node[MARK_UNDERLINE_COLOR] as any).colorKey;
        }
        if (node?.[MARK_STRIKETHROUGH]) {
          self.isStrikeThrough = true;
        }
        if (node?.[MARK_TEXT_COLOR]) {
          self.currentTextColor = (node[MARK_TEXT_COLOR] as any).colorKey || '';
        }
        if (node?.[MARK_CITE]) {
          self.isCite = true;
        }
        if (node?.[MARK_ABBR]) {
          self.isAbbr = true;
        }
        if (node?.[MARK_Q]) {
          self.isQ = true;
        }

        if (node?.[MARK_TEXT_TRANSFORM]) {
          switch (node[MARK_TEXT_TRANSFORM]) {
            case TEXT_TRANSFORM_UPPER_CASE: {
              self.isUpperCase = true;
              break;
            }
            case TEXT_TRANSFORM_LOWER_CASE: {
              self.isLowerCase = true;
              break;
            }
            case TEXT_TRANSFORM_TITLE_CASE: {
              self.isTitleCase = true;
              break;
            }
          }
        }
      }
    },
    tableSelectionCheck({ node, path }) {
      switch (node?.type) {
        case TableElement.TABLE: {
          self.isInTable = true;
          break;
        }
        case TableElement.TH: {
          self.isInTableHeader = true;
          break;
        }
        case TableElement.TD: {
          self.isInTableCell = true;
          break;
        }
        default:
      }
    },
    cardLogicSelectionCheck({ node, path }) {
      switch (node?.type) {
        case CARD_LOGIC_ELEMENT_TYPE: {
          self.isInCardLogic = true;
          break;
        }
        case CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE: {
          self.isInCardLogicHeader = true;
          break;
        }
        case CARD_LOGIC_CELL_ELEMENT_TYPE: {
          self.isInCardLogicCell = true;
          break;
        }
        default:
      }
    },
    borderShadingSelectionCheck({ node }) {
      if (node?.type === ELEMENT_BORDER_SHADING) {
        self.isInBorderShading = true;
      }
    },
    vocabCardCheck({ node }) {
      if (node?.type === VOCAB_CARD) {
        self.isDisableAll = true;
      }
    },
    disableAll(disable = true) {
      self.isDisableAll = disable;
    },
  }));

export interface BlogEditorSelectStateStoreInstance
  extends Instance<typeof BlogEditorSelectStateStore> {}
